import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sun, ArrowLeft, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const IMG =
  "https://images.unsplash.com/photo-1622598453695-4fbaf151aadc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxjaHVyY2glMjB3b3JzaGlwJTIwY29tbXVuaXR5JTIwZ2F0aGVyaW5nfGVufDB8fHx8MTc4MzkwNDY0Nnww&ixlib=rb-4.1.0&q=85";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated! Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFA] font-body">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/login" data-testid="back-login-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-amber-700 transition-colors duration-200">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
          <div className="mt-8">
            <img src="/logo.png" alt="Tennessee Web & Design" className="h-12 w-auto" />
          </div>

          {!token ? (
            <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="reset-invalid">
              <span className="grid place-items-center h-12 w-12 rounded-full bg-red-50 text-red-600 mx-auto"><ShieldAlert className="h-6 w-6" /></span>
              <h1 className="mt-5 font-display text-2xl font-bold text-slate-900">Invalid reset link</h1>
              <p className="mt-2 text-slate-600">This link is missing or malformed. Please request a new one.</p>
              <Link to="/forgot-password" className="mt-6 inline-block rounded-full bg-amber-600 hover:bg-amber-700 text-white font-semibold px-6 py-3 transition-colors duration-200">Request new link</Link>
            </div>
          ) : (
            <>
              <h1 className="mt-8 font-display text-3xl font-bold text-slate-900 tracking-tight">Set a new password</h1>
              <p className="mt-2 text-slate-600">Choose a strong password to secure your account.</p>
              <form onSubmit={submit} data-testid="reset-form" className="mt-8 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">New Password</label>
                  <input required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" className="auth-input" placeholder="At least 6 characters" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm Password</label>
                  <input required type="password" minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" className="auth-input" placeholder="Re-enter password" />
                </div>
                <button type="submit" disabled={loading} data-testid="reset-submit-btn" className="w-full rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-semibold py-3.5 transition-colors duration-200">
                  {loading ? "Updating..." : "Update Password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      <div className="hidden lg:block relative grain order-1 lg:order-2">
        <img src={IMG} alt="Worship community" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-slate-900/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <Sun className="h-10 w-10 text-amber-400" />
          <h2 className="mt-5 font-display text-4xl font-bold leading-tight">
            {'"Behold, I make all things new."'}
          </h2>
          <p className="mt-2 text-slate-300">Revelation 21:5 — A fresh start awaits.</p>
        </div>
      </div>

      <style>{`
        .auth-input { width:100%; border:1px solid hsl(214 20% 90%); border-radius:0.6rem; padding:0.75rem 0.9rem; font-size:0.9rem; background:#fff; box-shadow: inset 0 1px 2px rgba(0,0,0,0.03); transition:border-color .2s, box-shadow .2s; }
        .auth-input:focus { outline:none; border-color:hsl(32 95% 44%); box-shadow:0 0 0 3px hsl(32 95% 44% / 0.15); }
      `}</style>
    </div>
  );
}
